
import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Download, AlertCircle, CheckCircle } from "lucide-react";
import Papa from "papaparse";
import { useOrganization } from "@/contexts/OrganizationContext";

interface ImportContactsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
}

interface ParsedContact {
  name: string;
  email: string;
  phone_number?: string;
  company_name?: string;
  designation?: string;
  contact_type: 'Marketing' | 'Finance' | 'Legal' | 'Logistics' | 'Vendor' | 'Other';
  linked_brand_ids?: string[];
  linked_campaign_ids?: string[];
}

interface ValidationResult {
  valid: ParsedContact[];
  invalid: Array<{ row: number; errors: string[]; data: any }>;
}

const SAMPLE_CSV_DATA = `name,email,phone_number,company_name,designation,contact_type,linked_brand_ids,linked_campaign_ids
John Smith,john@company.com,+919876543210,Tech Corp,Marketing Manager,Marketing,,
Jane Doe,jane@startup.io,+919876543211,Startup Inc,Finance Head,Finance,,
Mike Johnson,mike@agency.com,+919876543212,Creative Agency,Creative Director,Other,,`;

export function ImportContactsModal({ open, onOpenChange, onImported }: ImportContactsModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { currentOrganization } = useOrganization();

  const validateRow = (row: any, rowIndex: number): { valid: boolean; contact?: ParsedContact; errors: string[] } => {
    const errors: string[] = [];
    
    // Required fields validation
    if (!row.name?.trim()) errors.push('Name is required');
    if (!row.email?.trim()) errors.push('Email is required');
    
    // Email validation
    if (row.email && !/^[\w\-.]+@([\w-]+\.)+[\w-]{2,}$/.test(row.email.trim())) {
      errors.push('Invalid email format');
    }
    
    // Phone validation (optional but if provided, should be reasonable)
    if (row.phone_number && !/^\+?[\d\s\-()]{10,15}$/.test(row.phone_number.trim())) {
      errors.push('Invalid phone number format');
    }
    
    // Contact type validation - default to 'Other' if not provided or invalid
    const validContactTypes = ['Marketing', 'Finance', 'Legal', 'Logistics', 'Vendor', 'Other'];
    let contactType: 'Marketing' | 'Finance' | 'Legal' | 'Logistics' | 'Vendor' | 'Other' = 'Other';
    
    if (row.contact_type && validContactTypes.includes(row.contact_type)) {
      contactType = row.contact_type;
    } else if (row.contact_type && !validContactTypes.includes(row.contact_type)) {
      errors.push('Contact type must be one of: Marketing, Finance, Legal, Logistics, Vendor, Other');
    }

    if (errors.length > 0) {
      return { valid: false, errors };
    }

    // Parse and clean data
    const contact: ParsedContact = {
      name: row.name.trim(),
      email: row.email.trim(),
      phone_number: row.phone_number?.trim() || undefined,
      company_name: row.company_name?.trim() || undefined,
      designation: row.designation?.trim() || undefined,
      contact_type: contactType,
      linked_brand_ids: row.linked_brand_ids ? row.linked_brand_ids.split(',').map((id: string) => id.trim()).filter(Boolean) : [],
      linked_campaign_ids: row.linked_campaign_ids ? row.linked_campaign_ids.split(',').map((id: string) => id.trim()).filter(Boolean) : [],
    };

    return { valid: true, contact, errors: [] };
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      toast({
        title: "Invalid file type",
        description: "Please select a CSV file.",
        variant: "destructive",
      });
      return;
    }

    setFile(selectedFile);
    
    // Parse CSV for preview
    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as any[];
        setHeaders(Object.keys(data[0] || {}));
        setPreviewData(data.slice(0, 5)); // Show first 5 rows
        
        // Validate all rows
        const validation: ValidationResult = { valid: [], invalid: [] };
        
        data.forEach((row, index) => {
          const result = validateRow(row, index);
          if (result.valid && result.contact) {
            validation.valid.push(result.contact);
          } else {
            validation.invalid.push({
              row: index + 1,
              errors: result.errors,
              data: row
            });
          }
        });
        
        setValidationResult(validation);
      },
      error: (error) => {
        toast({
          title: "Error parsing CSV",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  };

  const downloadSampleCSV = () => {
    const blob = new Blob([SAMPLE_CSV_DATA], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'contacts_sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!validationResult?.valid.length) {
      toast({
        title: "No valid data to import",
        description: "Please fix validation errors first.",
        variant: "destructive",
      });
      return;
    }

    if (!currentOrganization?.id) {
      toast({
        title: "Error",
        description: "No organization selected. Please select an organization first.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Add organization_id and created_by to all contact records
      const contactsWithOrgId = validationResult.valid.map(contact => ({
        ...contact,
        organization_id: currentOrganization.id,
        created_by: user.id
      }));

      const { data, error } = await supabase
        .from('contacts')
        .insert(contactsWithOrgId)
        .select();

      if (error) {
        console.error('Import error:', error);
        toast({
          title: "Import failed",
          description: error.message || "Failed to import contacts.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Import successful!",
        description: `Successfully imported ${data?.length || validationResult.valid.length} contacts.`,
      });

      onImported();
      onOpenChange(false);
      resetModal();
    } catch (err: any) {
      console.error('Import error:', err);
      toast({
        title: "Import failed",
        description: err.message || "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetModal = () => {
    setFile(null);
    setPreviewData([]);
    setHeaders([]);
    setValidationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    resetModal();
  };

  if (!currentOrganization) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Contacts from CSV</DialogTitle>
          </DialogHeader>
          <div className="p-6 text-center">
            <p className="text-gray-600">Please select an organization first to import contacts.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Contacts from CSV</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* File Upload Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Upload CSV File</h3>
              <Button variant="outline" size="sm" onClick={downloadSampleCSV}>
                <Download className="w-4 h-4 mr-2" />
                Download Sample CSV
              </Button>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Select a CSV file with contact data
                </p>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="max-w-xs mx-auto"
                />
              </div>
            </div>
            
            {/* Instructions */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">CSV Format Requirements:</h4>
              <p className="text-sm text-blue-800 mb-2">
                Required columns: <code>name</code>
              </p>
              <p className="text-sm text-blue-800">
                Optional columns: <code>email</code>, <code>phone_number</code>, <code>company_name</code>, 
                <code>designation</code>, <code>contact_type</code>, <code>linked_brand_ids</code>, <code>linked_campaign_ids</code>
              </p>
            </div>
          </div>

          {/* Preview Section */}
          {previewData.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Preview (First 5 Rows)</h3>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {headers.map((header) => (
                        <th key={header} className="px-4 py-2 text-left font-medium text-gray-900">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, index) => (
                      <tr key={index} className="border-b">
                        {headers.map((header) => (
                          <td key={header} className="px-4 py-2 text-gray-700">
                            {row[header] || '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Validation Results */}
          {validationResult && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Validation Results</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-900">Valid Rows</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    {validationResult.valid.length}
                  </p>
                </div>
                
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-red-900">Invalid Rows</span>
                  </div>
                  <p className="text-2xl font-bold text-red-600">
                    {validationResult.invalid.length}
                  </p>
                </div>
              </div>

              {/* Show validation errors */}
              {validationResult.invalid.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-medium text-red-900 mb-2">Validation Errors:</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {validationResult.invalid.slice(0, 10).map((error, index) => (
                      <div key={index} className="text-sm text-red-800">
                        <strong>Row {error.row}:</strong> {error.errors.join(', ')}
                      </div>
                    ))}
                    {validationResult.invalid.length > 10 && (
                      <p className="text-sm text-red-600">
                        ... and {validationResult.invalid.length - 10} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={!validationResult?.valid.length || isUploading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isUploading ? "Importing..." : `Import ${validationResult?.valid.length || 0} Contacts`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
